/**
 * AudioWorklet module loader.
 *
 * Registers a worklet processor source (see `worklet-sources.ts`) with an
 * `AudioContext` exactly once, turning the source string into a Blob URL. Blob
 * URLs are cached per source name and processors per context, so repeated calls
 * are cheap and idempotent.
 */

const registered = new WeakMap<BaseAudioContext, Set<string>>();
const blobUrls = new Map<string, string>();

/**
 * Ensure the named worklet processor is registered on `ctx`. Safe to call
 * repeatedly; the underlying `addModule` runs at most once per context/name.
 */
export async function ensureWorklet(
  ctx: BaseAudioContext,
  name: string,
  source: string,
): Promise<void> {
  let names = registered.get(ctx);
  if (!names) {
    names = new Set<string>();
    registered.set(ctx, names);
  }
  if (names.has(name)) return;

  let url = blobUrls.get(name);
  if (!url) {
    url = URL.createObjectURL(new Blob([source], { type: "application/javascript" }));
    blobUrls.set(name, url);
  }
  await ctx.audioWorklet.addModule(url);
  names.add(name);
}
